import dayjs, { Dayjs } from 'dayjs';

import { DB_DATE_TIME_SHORT_FORMAT } from 'core/constants';
import { CorporatePartCostStatus } from 'corporate/enums';
import {
  ICorporatePart,
  ICorporatePartPriceListItem,
  IGetActiveOverlappingPricePayload,
  IUpsertCorporatePartPricePayload,
} from 'corporate/types';

import { ICorporatePartPriceItemForm, IUpsertCorporatePartPriceForm } from './types';

const formatRequiredDate = (value: Dayjs): string => dayjs(value).format(DB_DATE_TIME_SHORT_FORMAT);

const formatOptionalDate = (value?: Dayjs | null): string | null => {
  if (!value) {
    return null;
  }

  return dayjs(value).format(DB_DATE_TIME_SHORT_FORMAT);
};

// BE derives the parent reference from the wrapping payload — sending
// corporatePartPriceId on items leaks the previous record's id on clone-and-replace.
const mapPriceItem = (item: ICorporatePartPriceItemForm, isPendingEdit: boolean) => ({
  ...(isPendingEdit && item.id && { id: item.id }),
  minQuantity: String(item.minQuantity),
  maxQuantity: item.maxQuantity != null ? String(item.maxQuantity) : null,
  marginReduction: item.marginReduction != null ? String(item.marginReduction) : null,
  discountIncrease: item.discountIncrease != null ? String(item.discountIncrease) : null,
});

// PENDING edit = in-place update (send id, no status, preserve item ids).
// Anything else (Add or Active edit) = clone-and-replace: send no id, status='active'.
// removedItems is only relevant for save (PENDING edits delete tier rows by id);
// the overlap-check endpoint ignores it (BE confirmed: only reads corporatePartId + startDate).
const buildBasePricePayload = (
  data: IUpsertCorporatePartPriceForm,
  corporatePart: Partial<ICorporatePart>,
  price?: ICorporatePartPriceListItem
): IGetActiveOverlappingPricePayload => {
  const isPendingEdit = price?.status === CorporatePartCostStatus.PENDING;

  return {
    ...(isPendingEdit && price?.id && { id: price.id }),
    startDate: formatRequiredDate(data.startDate),
    endDate: formatOptionalDate(data.endDate),
    priceItems: data.priceItems.map((item) => mapPriceItem(item, isPendingEdit)),
    corporatePartId: corporatePart.id,
    part: corporatePart.part,
    manufacturerId: corporatePart.manufacturerId,
    ...(!isPendingEdit && { status: CorporatePartCostStatus.ACTIVE }),
  };
};

export const mapUpsertCorporatePartPrice = (
  data: IUpsertCorporatePartPriceForm,
  corporatePart: Partial<ICorporatePart>,
  price?: ICorporatePartPriceListItem,
  removedItems: number[] = []
): IUpsertCorporatePartPricePayload => {
  const isPendingEdit = price?.status === CorporatePartCostStatus.PENDING;

  return {
    ...buildBasePricePayload(data, corporatePart, price),
    ...(isPendingEdit && removedItems.length > 0 && { removedItems }),
  };
};

export const mapActiveOverlappingPricePayload = (
  data: IUpsertCorporatePartPriceForm,
  corporatePart: Partial<ICorporatePart>,
  price?: ICorporatePartPriceListItem
): IGetActiveOverlappingPricePayload => buildBasePricePayload(data, corporatePart, price);

export const getDefaultValues = (
  corporatePart: Partial<ICorporatePart>,
  price?: ICorporatePartPriceListItem
): IUpsertCorporatePartPriceForm => {
  if (price) {
    const isPendingEdit = price.status === CorporatePartCostStatus.PENDING;

    return {
      startDate: isPendingEdit && price.startDate ? dayjs(price.startDate) : dayjs(),
      endDate: price.endDate ? dayjs(price.endDate) : null,
      priceItems: price.priceItems?.map((item) => ({
        id: item.id,
        minQuantity: item.minQuantity != null ? Number(item.minQuantity) : undefined,
        maxQuantity: item.maxQuantity != null ? Number(item.maxQuantity) : undefined,
        marginReduction: item.marginReduction != null ? Number(item.marginReduction) : undefined,
        discountIncrease: item.discountIncrease != null ? Number(item.discountIncrease) : undefined,
      })) ?? [
        {
          minQuantity: undefined,
          maxQuantity: undefined,
          marginReduction: undefined,
          discountIncrease: undefined,
        },
      ],
    };
  }

  return {
    startDate: dayjs(),
    endDate: null,
    priceItems: [
      {
        minQuantity: corporatePart?.orderIncrement ?? 1,
        maxQuantity: undefined,
        marginReduction: undefined,
        discountIncrease: undefined,
      },
    ],
  };
};
